import { z } from 'zod';

/**
 * `POST /api/generate` request body. v0 supports the four legacy generation
 * tasks; the backend stub returns a deterministic placehold.co URL keyed on
 * the prompt + productUid. Real AI provider integration (BFL Flux 2 Pro)
 * lands behind the same wire shape per ADR 0002.
 */
export const GENERATE_TASKS = ['enhance', 'edit', 'match-style', 'merge'] as const;
export const GenerateTaskSchema = z.enum(GENERATE_TASKS);
export type GenerateTask = z.infer<typeof GenerateTaskSchema>;

/** Prompt is optional for Enhance and Merge; required for Edit and Match Style. */
export const PROMPT_MIN_LENGTH = 0;
export const PROMPT_MAX_LENGTH = 2000;

/**
 * Output settings on a Photo Studio generation. Defaults preserve v0
 * behavior so existing call sites without these fields keep working
 * (`original` ratio, `standard` size, batch=1).
 *
 * Wire vocabulary is user-facing (`standard` / `large` / `xl`). The
 * internal `SizeTier` type ('1x' / '2x' / '3x') in generation.ts is the
 * credit multiplier; map standard->1x, large->2x, xl->3x at the
 * provider boundary.
 */
export const OUTPUT_SIZE_TIERS = ['standard', 'large', 'xl'] as const;
export const OutputSizeTierSchema = z.enum(OUTPUT_SIZE_TIERS);
export type OutputSizeTier = z.infer<typeof OutputSizeTierSchema>;

export const ASPECT_RATIOS = [
  /**
   * Match Look only — use the template image's natural aspect ratio.
   * The frontend computes naturalWidth/naturalHeight once the template
   * loads; the BFL provider resolves it at the provider boundary.
   * Outside Match Look this value is never sent in the wire shape.
   */
  'template',
  'original',
  '1:1',
  '4:3',
  '3:4',
  '16:9',
  '9:16',
  '3:2',
  '2:3',
  '21:9',
  '9:21',
] as const;
export const AspectRatioSchema = z.enum(ASPECT_RATIOS);
export type AspectRatio = z.infer<typeof AspectRatioSchema>;

export const BATCH_SIZE_MIN = 1;
export const BATCH_SIZE_MAX = 4;

/**
 * Match Look payload (iter 35). Template + product URLs + optional per-image
 * instructions for the Scene Match agent. Validated here; consumed by the
 * BFL provider once ADR 0002 is Accepted.
 */
export const MatchLookRequestSchema = z.object({
  subMode: z.enum(['style', 'scene']),
  templateImageUrl: z.string().url(),
  productImageUrls: z.array(z.string().url()).min(1).max(6),
  /** Keyed by productImageUrl. Scene Match agent receives them as per-image addenda. */
  perImageInstructions: z.record(z.string()).optional(),
});
export type MatchLookRequest = z.infer<typeof MatchLookRequestSchema>;

/**
 * Merge Images payload (iter 27). All input product image URLs plus an
 * optional scene pointer for Add to Scene sub-mode. Validated here;
 * consumed by the BFL provider once ADR 0002 is Accepted.
 */
export const MergeRequestSchema = z.object({
  subMode: z.enum(['create-scene', 'add-to-scene']),
  /** All input images (2-8). In add-to-scene, sceneImageUrl points to one of these. */
  productImageUrls: z.array(z.string().url()).min(2).max(8),
  /** Which URL is the background scene (add-to-scene only). */
  sceneImageUrl: z.string().url().optional(),
});
export type MergeRequest = z.infer<typeof MergeRequestSchema>;

/**
 * Enhance-mode settings. Controls which AI agent runs (Express = faster,
 * Art Director = higher fidelity) and the visual style applied.
 * Backend stub ignores; BFL + LLM providers use once ADR 0002 / ADR 0003 are Accepted.
 */
export const ENHANCE_PHOTOGRAPHERS = ['express', 'art-director'] as const;
export const EnhancePhotographerSchema = z.enum(ENHANCE_PHOTOGRAPHERS);
export type EnhancePhotographer = z.infer<typeof EnhancePhotographerSchema>;

export const ENHANCE_PHOTO_STYLES = [
  'auto',
  'professional',
  'creative',
  'natural',
  'bold',
  'elegant',
  'playful',
] as const;
export const EnhancePhotoStyleSchema = z.enum(ENHANCE_PHOTO_STYLES);
export type EnhancePhotoStyle = z.infer<typeof EnhancePhotoStyleSchema>;

export const EnhanceRequestSchema = z.object({
  photographer: EnhancePhotographerSchema.default('express'),
  photoStyle: EnhancePhotoStyleSchema.default('auto'),
});
export type EnhanceRequest = z.infer<typeof EnhanceRequestSchema>;

export const GenerateRequestSchema = z
  .object({
    /** Product the generation is rooted to. Free-form to support draft uids later. */
    productUid: z.string().min(1).max(200),
    task: GenerateTaskSchema,
    /** Empty string allowed — Enhance and Merge treat prompt as optional. */
    prompt: z.string().min(PROMPT_MIN_LENGTH).max(PROMPT_MAX_LENGTH),
    aspectRatio: AspectRatioSchema.default('original'),
    sizeTier: OutputSizeTierSchema.default('standard'),
    batchSize: z.number().int().min(BATCH_SIZE_MIN).max(BATCH_SIZE_MAX).default(1),
    /** Present when task === 'enhance'. Backend stub ignores; BFL + LLM providers use. */
    enhance: EnhanceRequestSchema.optional(),
    /** Present when task === 'match-style'. Backend stub ignores; BFL provider uses. */
    matchLook: MatchLookRequestSchema.optional(),
    /** Present when task === 'merge'. Backend stub ignores; BFL provider uses. */
    merge: MergeRequestSchema.optional(),
  })
  .strict();

/**
 * Use the INPUT type so callers can omit fields that have defaults
 * (aspectRatio / sizeTier / batchSize). The OUTPUT type — what the
 * backend sees post-validation — has those fields filled in.
 */
export type GenerateRequest = z.input<typeof GenerateRequestSchema>;
export type GenerateRequestParsed = z.output<typeof GenerateRequestSchema>;

/** Credit multipliers for the three size tiers (Standard / Large / XL -> 1x / 2x / 3x). */
export const SIZE_TIER_CREDIT_MULTIPLIER: Record<OutputSizeTier, number> = {
  standard: 1,
  large: 2,
  xl: 3,
};
